import React, { useState } from 'react';
import { sendSignInLinkToEmail } from 'firebase/auth';
import { useLoadingStates } from '../../../hooks/useLoadingStates';
import { AuthMode } from './AuthModal';
import EmailInput from '../EmailInput';
import LoadingButton from '../LoadingButton';
import styles from './AuthModal.module.css';

interface EmailContentProps {
  onSwitchMode: (mode: AuthMode) => void;
  onSuccess: () => void;
}

const EmailContent: React.FC<EmailContentProps> = ({ onSwitchMode, onSuccess }) => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isEmailSent, setIsEmailSent] = useState(false);
  
  // Loading states
  const { withLoading, isLoading } = useLoadingStates();
  const isEmailLoading = isLoading('email');

  // Email validation
  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // Action code settings for email link
  const actionCodeSettings = {
    url: 'https://www.tioassist.io/',
    handleCodeInApp: true,
  };

  // Handle email sign in
  const handleEmailSignIn = async () => {
    // Clear previous messages
    setError('');
    setSuccessMessage('');

    // Validate email
    if (!email) {
      setError('Please enter your email address.');
      return;
    }

    if (!isValidEmail(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    try {
      await withLoading('email', async () => {
        const { getAuth } = await import('@/firebaseLazy');
        const auth = await getAuth();
        await sendSignInLinkToEmail(auth, email, actionCodeSettings);
        
        // Store email for sign-in completion
        window.localStorage.setItem('emailForSignIn', email);
        
        setSuccessMessage('Email sent successfully! Please check your inbox and click the link to sign in.');
        setError('');
        setIsEmailSent(true);
      });
    } catch (error: any) {
      console.error('Email sign-in error:', error);
      
      // Handle specific Firebase errors
      switch (error.code) {
        case 'auth/invalid-email':
          setError('Invalid email format. Please use a different method to sign up.');
          break;
        case 'auth/quota-exceeded':
          setError("Hmm, something went wrong. Please try a different login method while our Tio's fix your problem.");
          break;
        case 'auth/too-many-requests':
          setError('Too many requests. Please try again later.');
          break;
        default:
          setError(`Error: ${error.message}`);
      }
      setSuccessMessage('');
    }
  };

  // Handle input change
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    // Clear errors when user types
    if (error) {
      setError('');
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleEmailSignIn();
    }
  };

  return (
    <div className={styles.formContainer}>
      <div className={styles.mainContent}>
        {/* Header */}
        <div className={styles.authHeader}>
          <h1 className={styles.authTitle}>Continue with Email</h1>
          <p className={styles.authSubtitle}>
            Enter your email to sign in or create an account.
          </p>
        </div>

      {/* Email Form */}
      <form onSubmit={(e) => { e.preventDefault(); handleEmailSignIn(); }}>
        <div style={{ marginBottom: '1rem' }}>
          <EmailInput
            id="email-only-input"
            placeholder="Email address"
            value={email}
            onChange={handleEmailChange}
            onKeyDown={handleKeyDown}
            hasError={!!error}
            className={styles.formInput}
            autoComplete="email"
            autoFocus
          />
          {error && (
            <div className={styles.errorMessage}>
              {error}
            </div>
          )}
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className={styles.successMessage} style={{ marginBottom: '1rem' }}>
            {successMessage}
          </div>
        )}

        {/* Submit Button */}
        {!isEmailSent && (
          <LoadingButton
            variant="primary"
            onClick={handleEmailSignIn}
            loading={isEmailLoading}
            loadingText="Sending email..."
            className={styles.primaryButton}
            style={{ width: '100%', marginBottom: '1.5rem' }}
            disabled={!email}
          >
            Send Sign-In Link
          </LoadingButton>
        )}

        {/* Success Action */}
        {isEmailSent && (
          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <p style={{ marginBottom: '1rem', color: 'var(--c-text-secondary)' }}>
              Check your email and click the link to complete sign-in.
            </p>
            <button
              type="button"
              onClick={() => onSwitchMode('login')}
              className={styles.socialButton}
            >
              Back to Login Options
            </button>
          </div>
        )}
      </form>

        {/* Footer */}
        <div className={styles.formFooter}>
          <p className={styles.switchModeText}>
            Want to use a different method?{' '}
            <span 
              className={styles.switchModeLink} 
              onClick={() => onSwitchMode('login')}
            >
              Back to options
            </span>
          </p>
        </div>
      </div>

      {/* Terms - Pushed to Bottom */}
      <div className={styles.termsText}>
        By continuing, you agree to our{' '}
        <a href="/terms-of-service" target="_blank" rel="noopener">Terms of Use</a>.
        Read our{' '}
        <a href="/privacy-policy" target="_blank" rel="noopener">Privacy Policy</a>.
      </div>
    </div>
  );
};

export default EmailContent;